import { Routes } from '@angular/router';

import { ngxPermissionsGuard } from 'ngx-permissions';
import { PageName } from '~backoffice/app/~shared/models/shared.models';
import { Permission } from '~core/models/roles.models';
import { AuthLayoutComponent } from './auth/layouts/auth-layout/auth-layout.component';
import { ForgotPasswordComponent } from './auth/pages/forgot-password/forgot-password.component';
import { LoginComponent } from './auth/pages/login/login.component';
import { ResetPasswordComponent } from './auth/pages/reset-password/reset-password.component';
import { ConfigurationLayoutComponent } from './configuration/layouts/configuration-layout/configuration-layout.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { AUTHENTICATED_GUARD } from './~shared/guards/authenticated.guard';
import { NON_AUTHENTICATED_GUARD } from './~shared/guards/non-authenticated.guard';
import { ContentWrapperComponent } from './~shared/layouts/content-wrapper/content-wrapper.component';

export const routes: Routes = [
  {
    path: 'auth',
    component: AuthLayoutComponent,
    children: [
      {
        path: 'signin',
        canActivate: [NON_AUTHENTICATED_GUARD],
        children: [
          {
            path: '',
            component: LoginComponent,
            data: {
              pageName: PageName.login
            }
          },
          {
            path: 'otp',
            loadComponent: () =>
              import('./auth/pages/login-otp/login-otp.component').then(
                m => m.LoginOtpComponent
              ),
            data: {
              pageName: PageName.signInOTP
            }
          }
        ]
      },
      {
        path: 'forgot-password',
        canActivate: [NON_AUTHENTICATED_GUARD],
        component: ForgotPasswordComponent,
        data: {
          pageName: PageName.forgotPassword
        }
      },
      {
        path: 'reset-password',
        canActivate: [NON_AUTHENTICATED_GUARD],
        component: ResetPasswordComponent,
        data: {
          pageName: PageName.resetPassword
        }
      },
      {
        path: 'password/change',
        loadComponent: () =>
          import('./auth/pages/password-init/password-init.component').then(
            m => m.PasswordInitComponent
          ),
        data: {
          pageName: PageName.passwordInit
        }
      }
    ]
  },
  {
    path: '',
    component: ContentWrapperComponent,
    canActivate: [AUTHENTICATED_GUARD],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./dashboard/pages/dashboard/dashboard.component').then(
            m => m.DashboardComponent
          ),
        data: {
          pageName: PageName.dashboard,
          breadcrumbs: "Tableau de bord d'administration"
        }
      },
      {
        path: 'audit-logs',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./audit-logs/pages/audit-logs/audit-logs.component').then(
            m => m.AuditLogsComponent
          ),
        data: {
          pageName: PageName.auditLogs,
          breadcrumbs: 'Audit logs',
          permissions: {
            only: [Permission.READ_AUDIT_LOG],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'invoices',
        canActivate: [ngxPermissionsGuard],
        data: {
          pageName: PageName.invoices,
          breadcrumbs: 'Factures',
          permissions: {
            only: [Permission.READ_INVOICE],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./invoices/pages/invoice/invoice.component').then(
                m => m.InvoiceComponent
              ),
            data: {
              pageName: PageName.invoices,
              breadcrumbs: 'Gestion des Factures'
            }
          },
          {
            path: 'generate/:invoiceId',
            // canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./invoices/pages/invoices-generation/invoices-generation.component').then(
                m => m.InvoicesGenerationComponent
              ),
            data: {
              pageName: PageName.invoicesGeneration,
              breadcrumbs: 'Génération de la facture'
            }
          },
          {
            path: 'generate-credit-note/:invoiceId',
            loadComponent: () =>
              import('./invoices/pages/generation-credit-note/generation-credit-note.component').then(
                m => m.GenerationCreditNoteComponent
              ),
            data: {
              pageName: PageName.invoicesGenerationCreditNote,
              breadcrumbs: 'Génération de la facture'
            }
          }
        ]
      },
      {
        path: 'payment-receipts',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./receipt/pages/receipt-list-page/receipt-list-page.component').then(
            m => m.ReceiptListPageComponent
          ),
        data: {
          pageName: PageName.receiptList,
          breadcrumbs: 'Gestion des reçus',
          permissions: {
            only: [Permission.READ_RECEIPT],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'users',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumbs: 'Gestion des utilisateurs',
          permissions: {
            only: [Permission.READ_DGI_ADMIN],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            canActivate: [ngxPermissionsGuard],
            loadComponent: () =>
              import('./users/pages/users/users.component').then(
                m => m.UsersComponent
              ),
            data: {
              pageName: PageName.users,
              breadcrumbs: 'Gestion des utilisateurs',
              permissions: {
                only: [Permission.READ_DGI_ADMIN],
                redirectTo: '/'
              }
            }
          },
          {
            path: 'roles-and-permissions',
            canActivate: [ngxPermissionsGuard],
            loadComponent: () =>
              import('./users/pages/user-habilitations/user-habilitations.component').then(
                m => m.UserHabilitationsComponent
              ),
            data: {
              pageName: PageName.ability,
              breadcrumbs: 'Roles et permissions',
              permissions: {
                only: [Permission.READ_ROLE],
                redirectTo: '/'
              }
            }
          }
          // {
          //   path: ':userId/profils',
          //   loadComponent: () =>
          //     import(
          //       './users/pages/users-profile/users-profile.component'
          //     ).then(m => m.UsersProfileComponent),
          //   data: {
          //     pageName: PageName.userProfil,
          //     breadcrumbs: "Gestion d'administrateurs"
          //   }
          // },
        ]
      },
      {
        path: 'unite-mesures',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./unit-measure/pages/unit-measure/unit-measure.component').then(
            m => m.UnitMeasureComponent
          ),
        data: {
          pageName: PageName.unitMeasure,
          breadcrumbs: 'Unités de mesure',
          permissions: {
            only: [Permission.READ_UNIT_MEASURE],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'sales-point',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./sales-point/pages/sales-point/sales-point.component').then(
            m => m.SalesPointComponent
          ),
        data: {
          pageName: PageName.salesPoint,
          breadcrumbs: 'Points de vente',
          permissions: {
            only: [Permission.READ_SALE_POINT],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'stickers',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumbs: "Vue d'ensemble des stickers",
          pageName: PageName.stickers,
          permissions: {
            only: [Permission.READ_STICKER],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./stickers/page/stickers/stickers.component').then(
                m => m.StickersComponent
              )
          },
          {
            path: 'checkout/succeed',
            loadComponent: () =>
              import('./stickers/page/stickers-payment-succeed/stickers-payment-succeed.component').then(
                m => m.StickersPaymentSucceedComponent
              )
          }
        ]
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./settings/layouts/settings-layout.component').then(
            m => m.SettingsLayoutComponent
          ),
        data: {
          breadcrumbs: 'Gestion du compte'
        },
        children: [
          {
            path: 'account',
            loadComponent: () =>
              import('./settings/pages/settings-account/settings-account.component').then(
                m => m.SettingsAccountComponent
              ),
            data: {
              pageName: PageName.accountProfile,
              breadcrumbs: 'Informations du compte'
            }
          },
          {
            path: 'security',
            loadComponent: () =>
              import('./settings/pages/settings-security/settings-security.component').then(
                m => m.SettingsSecurityComponent
              ),
            data: {
              pageName: PageName.accountSecurity,
              breadcrumbs: 'Sécurité du compte'
            }
          },
          {
            path: 'enterprise-infos',
            loadComponent: () =>
              import('./settings/pages/settings-enterprise-infos/settings-enterprise-infos.component').then(
                m => m.SettingsEnterpriseInfosComponent
              ),
            data: {
              pageName: PageName.accountEnterpriseInfos,
              breadcrumbs: "Informations de l'entreprise"
            }
          }
        ]
      },
      {
        path: 'organizations',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumbs: 'Entreprises Inscrites à la FNE',
          permissions: {
            only: [Permission.READ_ORGANIZATION],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./organizations/pages/organization-list/organization-list-page.component').then(
                m => m.OrganizationListPageComponent
              ),
            data: {
              pageName: PageName.organizationList,
              headerTitle: 'Gestion des organisations'
            }
          },
          {
            path: ':organizationId',
            loadComponent: () =>
              import('./organizations/layouts/organizations-layout.component').then(
                m => m.OrganizationsLayoutComponent
              ),
            data: {
              headerTitle: "Gestion d'organisation"
            },
            children: [
              {
                path: '',
                redirectTo: 'invoices',
                pathMatch: 'full'
              },
              {
                path: 'invoices',
                loadComponent: () =>
                  import('./organizations/pages/organization-invoices/organization-invoices.component').then(
                    m => m.OrganizationInvoicesComponent
                  ),
                data: {
                  pageName: PageName.organizationInvoices,
                  breadcrumbs: 'Liste des factures'
                }
              },
              {
                path: 'users',
                loadComponent: () =>
                  import('./organizations/pages/organization-users/organization-users.component').then(
                    m => m.OrganizationUsersComponent
                  ),
                data: {
                  pageName: PageName.organizationUsers,
                  breadcrumbs: 'Liste des utilisateurs'
                }
              },
              {
                path: 'security',
                loadComponent: () =>
                  import('./organizations/pages/settings-security/organizations-settings-security.component').then(
                    m => m.OrganizationsSettingsSecurityComponent
                  ),
                data: {
                  pageName: PageName.organizationSecurity,
                  breadcrumbs: 'Paramètres de sécurité'
                }
              },
              {
                path: 'receipts',
                loadComponent: () =>
                  import('./organizations/pages/organization-receipt/organization-receipt.component').then(
                    m => m.OrganizationReceiptComponent
                  ),
                data: {
                  pageName: PageName.organizationReceipts,
                  breadcrumbs: 'Liste des reçus'
                }
              }
            ]
          }
        ]
      },
      {
        path: 'billing-equipments',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./billing-equipments/pages/billing-equipments/billing-equipments.component').then(
            m => m.BillingEquipmentsComponent
          ),
        data: {
          pageName: PageName.billingEquipments,
          breadcrumbs: 'Gestions des équipements de facturation',
          permissions: {
            only: [Permission.READ_REFERENCE_DATA],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'payments',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./payments/pages/payments/payments.component').then(
            m => m.PaymentsComponent
          ),
        data: {
          pageName: PageName.payments,
          breadcrumbs: 'Gestion des paiements',
          permissions: {
            only: [Permission.READ_PAYMENT],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'tax-profiles',
        canActivate: [ngxPermissionsGuard],
        data: {
          pageName: PageName.taxProfiles,
          breadcrumbs: 'Gestion des profils fiscaux',
          permissions: {
            only: [Permission.READ_TAX_PROFILE],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./tax-profile/pages/tax-profile-list-page/tax-profile-list-page.component').then(
                m => m.TaxProfileListPageComponent
              )
          },
          {
            path: ':taxProfileId/organizations',
            loadComponent: () =>
              import('./tax-profile/pages/tax-profile-organization-list/tax-profile-organization-list-page.component').then(
                m => m.TaxProfileOrganizationListPageComponent
              ),
            data: {
              pageName: PageName.taxProfileOrganizations,
              breadcrumbs: 'Entreprises du profil fiscal',
              permissions: {
                only: [Permission.READ_TAX_PROFILE],
                redirectTo: '/'
              }
            }
          }
        ]
      },
      {
        path: 'currencies',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./currency/pages/currency/currency.component').then(
            m => m.CurrencyComponent
          ),
        data: {
          pageName: PageName.currencies,
          breadcrumbs: 'Gestion des devises',
          permissions: {
            only: [Permission.READ_CURRENCY],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'taxes',
        canActivate: [ngxPermissionsGuard],
        // loadComponent: () =>
        //   import('./tax/layouts/tax-page-layout/tax-page-layout.component').then(
        //     m => m.TaxPageLayoutComponent
        //   ),
        data: {
          permissions: {
            only: [Permission.READ_TAX],
            redirectTo: '/'
          },
          breadcrumbs: 'Paramétrage des taxes'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./tax/pages/tax-list-page/tax-list-page.component').then(
                m => m.TaxListPageComponent
              ),
            pathMatch: 'full',
            data: {
              pageName: PageName.taxList
            }
          },
          {
            path: ':taxId/rules',
            loadComponent: () =>
              import('./tax/pages/tax-rules-page/tax-rules-page.component').then(
                m => m.TaxRulesPageComponent
              ),
            data: {
              pageName: PageName.taxRules,
              breadcrumbs: 'Gestion des règles'
            }
          }
        ]
      },
      {
        path: 'tax-types',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./tax-types/pages/tax-types-list-page/tax-types-list-page.component').then(
            m => m.TaxTypesListPageComponent
          ),
        data: {
          pageName: PageName.taxTypes,
          breadcrumbs: 'Gestion des types de taxes',
          permissions: {
            only: [Permission.READ_TAX_TYPE],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'dgi-settings',
        canActivate: [AUTHENTICATED_GUARD],
        data: {
          breadcrumbs: 'Paramètres espace DGI'
        },
        component: ConfigurationLayoutComponent,
        children: [
          {
            path: 'dashboards',
            canActivate: [ngxPermissionsGuard],
            data: {
              pageName: PageName.dashboardList,
              permissions: {
                only: [Permission.READ_DGI_DASHBOARD_CONFIG],
                redirectTo: '/dgi-settings/security'
              }
            },
            loadComponent: () =>
              import('./configuration/pages/dashboard-list/dashboard-list-page.component').then(
                m => m.DashboardListPageComponent
              )
          },
          {
            path: 'security',
            canActivate: [ngxPermissionsGuard],
            data: {
              pageName: PageName.dgiSecurity,
              permissions: {
                only: [Permission.READ_DGI_SECURITY_CONFIG],
                redirectTo: '/'
              }
            },
            loadComponent: () =>
              import('./configuration/pages/security/security-page.component').then(
                m => m.SecurityPageComponent
              )
          }
        ]
      },
      {
        path: '**',
        // redirectTo: '/'
        loadComponent: () =>
          import('./not-found/not-found.component').then(
            m => m.NotFoundComponent
          )
      }
    ]
  },
  {
    path: '**',
    component: NotFoundComponent
  }
];
